import axios from 'axios';

// Define the Course Base URL
const COURSE_BASE_URL = 'https://studentapi.anginatlearning.com/api/course';

const CourseDetailService = {
  /**
   * Fetch course details by ID
   * @param {string} courseId - The ID of the course
   * @param {string} batchId - The ID of the batch
   * @returns {Promise<Object>} Raw API response containing course details
   */
  getCourseDetails: async (courseId, batchId) => {
    try {
      // Get the authentication token from localStorage
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      // Make the API call with proper headers
      const response = await axios({
        method: 'GET',
        url: `${COURSE_BASE_URL}/courses/${courseId}`,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      // Check if response has the expected structure
      if (response.data && response.data.status === 'success') {
        console.log('Successfully fetched course details:', response.data);
        
        // Add batch information for this specific enrollment
        const batchInfo = response.data.data.batches.find(batch => batch._id === batchId) || {};
        
        // Return the course data with the specific batch highlighted
        return {
          ...response.data,
          data: {
            ...response.data.data,
            currentBatch: batchInfo
          }
        };
      } else {
        console.error('Unexpected response format:', response.data);
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error fetching course details:', error);
      throw error;
    }
  },

  /**
   * Mock function to provide sample course details when API is not available
   * @returns {Object} Sample course details
   */
  getMockCourseDetails: (courseId, batchId) => {
    console.log(`Getting mock course details for courseId: ${courseId}, batchId: ${batchId}`);
    
    // Define mock course data map
    const mockCoursesMap = {
      // PMP Course (from your real data)
      "67484e5cf99edee89752613a": {
        _id: "67484e5cf99edee89752613a",
        courseName: "PMP-Project Management Professional 7-Dec",
        courseCode: "AL_24_0001_24_0085",
        description: "Project Management course - This comprehensive course prepares you for the PMP certification exam. Learn project management methodologies, tools, and best practices as defined by the Project Management Institute.",
        thumbnail: "https://ik.imagekit.io/dgf4txxxo/thumbnails/thumbnail-1734089153060_sm2Bom9fQg.jpeg",
        batches: [
          {
            _id: "67dd4f2e1c8cf2e66213a7dc",
            name: "PMP 1-Apr",
            batchType: "weekdays",
            startDate: "2025-03-31T18:30:00.000Z",
            endDate: "2025-04-03T18:30:00.000Z",
            timeZone: "9:30 AM - 5:30 PM",
            location: "Online"
          }
        ],
        // curriculum: [
        //   { id: "module-1", title: "Introduction to Project Management", completed: true },
        //   { id: "module-2", title: "Project Integration Management", completed: false },
        //   { id: "module-3", title: "Project Scope Management", completed: false },
        //   { id: "module-4", title: "Project Schedule Management", completed: false },
        //   { id: "module-5", title: "Project Cost Management", completed: false }
        // ],
        // instructor: {
        //   name: "John Smith",
        //   bio: "PMP certified instructor with over 15 years of project management experience in IT, construction, and manufacturing industries.",
        //   avatar: "https://via.placeholder.com/100x100"
        // }
      },
      
      // React Course
      "react-course-123": {
        _id: "react-course-123",
        courseName: "Advanced React Development",
        description: "Master React with advanced concepts including hooks, context API, Redux, and performance optimization techniques. Create scalable React applications using best practices.",
        thumbnail: "https://via.placeholder.com/400x225/61DAFB/000000?text=React+Advanced",
        batches: [
          {
            _id: "weekend-batch-456",
            name: "Weekend Batch",
            batchType: "weekend",
            startDate: "2025-04-05T10:00:00.000Z",
            endDate: "2025-06-28T14:00:00.000Z",
            timeZone: "10:00 AM - 2:00 PM",
            location: "Zoom"
          }
        ],
        curriculum: [
          { id: "react-1", title: "Advanced Hooks", completed: true },
          { id: "react-2", title: "State Management with Context", completed: true },
          { id: "react-3", title: "Redux & Redux Toolkit", completed: false },
          { id: "react-4", title: "React Performance Optimization", completed: false },
          { id: "react-5", title: "Testing React Applications", completed: false }
        ],
        instructor: {
          name: "Emma Wilson",
          bio: "Frontend specialist with focus on React ecosystem. Previously worked at Facebook and has contributed to several open source React projects.",
          avatar: "https://via.placeholder.com/100x100"
        }
      },
      
      // Data Science Course
      "data-science-789": {
        _id: "data-science-789",
        courseName: "Data Science Fundamentals",
        description: "Introduction to data science covering Python programming, data analysis with pandas, visualization, and machine learning basics with scikit-learn.",
        thumbnail: "https://via.placeholder.com/400x225/33FF99/000000?text=Data+Science",
        batches: [
          {
            _id: "evening-batch-789",
            name: "Evening Batch",
            batchType: "weekday",
            startDate: "2025-05-01T18:00:00.000Z",
            endDate: "2025-07-31T20:00:00.000Z",
            timeZone: "6:00 PM - 8:00 PM",
            location: "Microsoft Teams"
          }
        ],
        curriculum: [
          { id: "ds-1", title: "Python Programming Basics", completed: true },
          { id: "ds-2", title: "Data Manipulation with Pandas", completed: false },
          { id: "ds-3", title: "Data Visualization with Matplotlib", completed: false },
          { id: "ds-4", title: "Introduction to Machine Learning", completed: false },
          { id: "ds-5", title: "Practical ML Projects", completed: false }
        ],
        instructor: {
          name: "Dr. Alex Chen",
          bio: "Data scientist with PhD in Statistics. Has worked on ML projects for healthcare and finance industries with focus on practical applications of AI.",
          avatar: "https://via.placeholder.com/100x100"
        }
      }
    };
    
    // Look up the course by ID
    const course = mockCoursesMap[courseId];
    
    if (!course) {
      console.error(`No mock course found for ID: ${courseId}`);
      throw new Error(`Course not found with ID: ${courseId}`);
    }
    
    // Find the batch in the course
    const batchInfo = course.batches.find(batch => batch._id === batchId);
    
    if (!batchInfo) {
      console.error(`No batch found with ID: ${batchId} in course: ${courseId}`);
      throw new Error(`Batch not found with ID: ${batchId} in course: ${courseId}`);
    }
    
    // Return the course with the current batch
    return {
      status: "success",
      message: "Course details fetched successfully (Mock Data)",
      data: {
        ...course,
        currentBatch: batchInfo
      }
    };
  }
};

export default CourseDetailService;